import { useEffect, useState } from "react";
import { useContent } from "@/content/ContentContext";
import { LogOut, Save, Trash2, Plus, Check, ExternalLink, Hammer } from "lucide-react";
import { SeoPanel, ProjectsPanel, CustomersPanel, MessagesPanel } from "./AdminPanels";
import { SERVICE_ICONS, ICON_COMPONENTS } from "@/content/serviceIcons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-foreground/15 bg-background px-3 py-2.5 text-sm text-foreground outline-none transition-colors duration-300 placeholder:text-muted-foreground/50 focus:border-primary";
const labelCls =
  "mb-1.5 block font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground";

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Etwas ist schiefgelaufen. Bitte noch mal versuchen.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const Field = ({ label, value, onChange, textarea = false, rows = 3 }) => (
  <div>
    <label className={labelCls}>{label}</label>
    {textarea ? (
      <textarea
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`${inputCls} resize-y`}
      />
    ) : (
      <input value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} />
    )}
  </div>
);

const BlocksEditor = ({ blocks, onChange }) => {
  const update = (i, key, value) =>
    onChange(blocks.map((b, j) => (j === i ? { ...b, [key]: value } : b)));
  return (
    <div className="space-y-4">
      {blocks.map((b, i) => (
        <div key={i} className="space-y-3 border border-foreground/10 p-4">
          <div className="flex items-center gap-3">
            <input
              value={b.h}
              onChange={(e) => update(i, "h", e.target.value)}
              className={inputCls}
              aria-label="Abschnitt-Titel"
            />
            <button
              type="button"
              onClick={() => onChange(blocks.filter((_, j) => j !== i))}
              className="shrink-0 rounded-full border border-foreground/15 p-2 text-muted-foreground transition-colors duration-300 hover:border-primary hover:text-primary"
              aria-label="Abschnitt löschen"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
          <textarea
            rows={3}
            value={b.p.join("\n")}
            onChange={(e) => update(i, "p", e.target.value.split("\n"))}
            className={`${inputCls} resize-y`}
            aria-label="Abschnitt-Text, eine Zeile pro Absatz"
          />
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...blocks, { h: "Neuer Abschnitt", p: [""] }])}
        className="inline-flex items-center gap-2 border border-foreground/15 px-4 py-2 font-mono text-xs uppercase tracking-wider text-muted-foreground transition-colors duration-300 hover:border-primary hover:text-primary"
      >
        <Plus className="h-4 w-4" /> Abschnitt hinzufügen
      </button>
    </div>
  );
};

const TABS = [
  { id: "hero", label: "Hero" },
  { id: "about", label: "Über mich" },
  { id: "services", label: "Leistungen" },
  { id: "beforeAfter", label: "Vorher / Nachher" },
  { id: "contact", label: "Kontakt" },
  { id: "businessCard", label: "Visitenkarte" },
  { id: "legal", label: "Impressum & Datenschutz" },
  { id: "seo", label: "SEO / Meta" },
  { id: "projekte", label: "Projekte" },
  { id: "kunden", label: "Kunden" },
  { id: "inquiries", label: "Anfragen" },
  { id: "nachrichten", label: "Portal-Nachrichten" },
  { id: "konto", label: "Konto" },
];

const CONTENT_TABS = ["hero", "about", "services", "beforeAfter", "contact", "businessCard", "legal", "seo"];

export default function Admin() {
  const content = useContent();
  const [token, setToken] = useState(() => localStorage.getItem("rundum_admin_token"));
  const [checking, setChecking] = useState(!!token);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [tab, setTab] = useState("hero");
  const [draft, setDraft] = useState(null);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [inquiries, setInquiries] = useState(null);
  const [pwForm, setPwForm] = useState({ current: "", next: "" });
  const [pwMsg, setPwMsg] = useState("");
  const [unread, setUnread] = useState(0);

  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/auth/me`, { headers: authHeaders })
      .then((r) => {
        if (!r.ok) throw new Error("unauthorized");
        setChecking(false);
      })
      .catch(() => {
        localStorage.removeItem("rundum_admin_token");
        setToken(null);
        setChecking(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!token) return;
    const fetchUnread = () =>
      fetch(`${API}/admin/messages`, { headers: authHeaders })
        .then((r) => (r.ok ? r.json() : { messages: [] }))
        .then((d) => setUnread(d.messages.filter((m) => !m.read).length))
        .catch(() => {});
    fetchUnread();
    const iv = setInterval(fetchUnread, 15000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (token && !draft) setDraft(structuredClone(content));
  }, [token, content, draft]);

  useEffect(() => {
    if (token && tab === "inquiries") {
      fetch(`${API}/inquiries`, { headers: authHeaders })
        .then((r) => (r.ok ? r.json() : { inquiries: [] }))
        .then((d) => setInquiries(d.inquiries))
        .catch(() => setInquiries([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, tab]);

  const set = (path, value) =>
    setDraft((d) => {
      const copy = structuredClone(d);
      let o = copy;
      for (let i = 0; i < path.length - 1; i++) o = o[path[i]];
      o[path[path.length - 1]] = value;
      return copy;
    });

  const login = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const r = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(formatApiErrorDetail(data.detail));
      localStorage.setItem("rundum_admin_token", data.access_token);
      setToken(data.access_token);
    } catch (err) {
      setLoginError(err.message);
    }
  };

  const save = async () => {
    setSaved(false);
    setSaveError("");
    const r = await fetch(`${API}/content`, {
      method: "PUT",
      headers: { ...authHeaders, "Content-Type": "application/json" },
      body: JSON.stringify({ content: draft }),
    });
    if (r.ok) {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } else {
      setSaveError("Speichern fehlgeschlagen. Bitte noch mal versuchen.");
    }
  };

  const logout = () => {
    localStorage.removeItem("rundum_admin_token");
    setToken(null);
    setDraft(null);
  };

  const deleteInquiry = async (id) => {
    await fetch(`${API}/inquiries/${id}`, { method: "DELETE", headers: authHeaders });
    setInquiries((list) => list.filter((i) => i.id !== id));
  };

  const changePw = async (e) => {
    e.preventDefault();
    setPwMsg("");
    const r = await fetch(`${API}/auth/change-password`, {
      method: "POST",
      headers: { ...authHeaders, "Content-Type": "application/json" },
      body: JSON.stringify({ current_password: pwForm.current, new_password: pwForm.next }),
    });
    const data = await r.json().catch(() => ({}));
    setPwMsg(r.ok ? "Passwort geändert." : formatApiErrorDetail(data.detail));
    if (r.ok) setPwForm({ current: "", next: "" });
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">Lade …</p>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-sm space-y-5 border border-foreground/10 bg-card p-8"
        >
          <img src="/rundum-logo.png" alt="Rundum Logo" className="h-10 w-auto object-contain object-left" />
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">Admin-Bereich</p>
          <div>
            <label htmlFor="admin-username" className={labelCls}>Benutzername</label>
            <input
              id="admin-username"
              data-testid="admin-username-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              className={inputCls}
              autoComplete="username"
            />
          </div>
          <div>
            <label htmlFor="admin-password" className={labelCls}>Passwort</label>
            <input
              id="admin-password"
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className={inputCls}
              autoComplete="current-password"
            />
          </div>
          {loginError && (
            <p className="text-sm text-primary" data-testid="admin-login-error">{loginError}</p>
          )}
          <button
            type="submit"
            data-testid="admin-login-button"
            className="w-full bg-primary px-6 py-3.5 font-heading text-sm font-bold uppercase tracking-wider text-primary-foreground transition-colors duration-300 hover:bg-foreground hover:text-background"
          >
            Einloggen
          </button>
          <a href="/" className="block text-center font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground transition-colors duration-300 hover:text-primary">
            Zur Website
          </a>
        </form>
      </div>
    );
  }

  if (!draft) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">Lade Inhalte …</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-foreground/10 bg-background/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-6 py-4">
          <div className="flex items-center gap-4">
            <img src="/rundum-logo.png" alt="Rundum Logo" className="h-8 w-auto object-contain object-left" />
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground transition-colors duration-300 hover:text-primary"
            >
              <ExternalLink className="h-3.5 w-3.5" /> Website
            </a>
            {CONTENT_TABS.includes(tab) && (
              <button
                onClick={save}
                data-testid="admin-save-button"
                className="inline-flex items-center gap-2 bg-primary px-5 py-2.5 font-heading text-xs font-bold uppercase tracking-wider text-primary-foreground transition-colors duration-300 hover:bg-foreground hover:text-background"
              >
                {saved ? <Check className="h-4 w-4" /> : <Save className="h-4 w-4" />}
                {saved ? "Gespeichert" : "Speichern"}
              </button>
            )}
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 border border-foreground/15 px-4 py-2.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground transition-colors duration-300 hover:border-primary hover:text-primary"
            >
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-6 py-8">
        <nav className="mb-8 flex flex-wrap gap-2" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                tab === t.id
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-foreground/15 text-muted-foreground hover:border-primary hover:text-primary"
              }`}
            >
              {t.label}
              {t.id === "nachrichten" && unread > 0 && (
                <span
                  className="ml-2 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 font-heading text-[10px] font-bold text-primary-foreground"
                  data-testid="unread-badge"
                >
                  {unread}
                </span>
              )}
            </button>
          ))}
        </nav>

        {saveError && <p className="mb-6 text-sm text-primary" data-testid="admin-save-error">{saveError}</p>}

        {tab === "hero" && (
          <div className="max-w-2xl space-y-5" data-testid="admin-panel-hero">
            <Field label="Kleine Zeile über der Überschrift" value={draft.hero.kicker} onChange={(v) => set(["hero", "kicker"], v)} />
            <Field label="Überschrift Zeile 1" value={draft.hero.line1} onChange={(v) => set(["hero", "line1"], v)} />
            <Field label="Überschrift Zeile 2 (orange)" value={draft.hero.line2} onChange={(v) => set(["hero", "line2"], v)} />
            <Field label="Überschrift Zeile 3" value={draft.hero.line3} onChange={(v) => set(["hero", "line3"], v)} />
            <Field label="Text unter der Überschrift" textarea rows={4} value={draft.hero.subline} onChange={(v) => set(["hero", "subline"], v)} />
            <Field label="Hintergrundbild-URL (Hero)" value={draft.hero.bg_url || ""} onChange={(v) => set(["hero", "bg_url"], v)} />
            {draft.hero.bg_url && (
              <img src={draft.hero.bg_url} alt="Vorschau Hintergrundbild" className="h-24 w-40 border border-foreground/10 object-cover" data-testid="hero-bg-preview" />
            )}
            <div>
              <label className={labelCls}>
                Sichtbarkeit des Hintergrundbilds ({Math.round((draft.hero.bg_opacity ?? 0.13) * 100)} %)
              </label>
              <input
                type="range"
                min="0"
                max="0.4"
                step="0.01"
                value={draft.hero.bg_opacity ?? 0.13}
                onChange={(e) => set(["hero", "bg_opacity"], parseFloat(e.target.value))}
                className="w-full accent-[hsl(15,80%,54%)]"
                data-testid="hero-opacity-slider"
              />
              <p className="mt-1 text-xs text-muted-foreground">0 % = unsichtbar, höher = kräftiger. Empfohlen: 10–15 %.</p>
            </div>
            <Field label="Laufband-Einträge oben (eine Zeile pro Eintrag)" textarea rows={6} value={draft.marquee.join("\n")} onChange={(v) => set(["marquee"], v.split("\n"))} />
            <label className="flex cursor-pointer items-center gap-3 text-sm text-foreground">
              <input
                type="checkbox"
                checked={draft.marqueeBottom.enabled}
                onChange={(e) => set(["marqueeBottom", "enabled"], e.target.checked)}
                className="h-4 w-4 accent-[hsl(15,80%,54%)]"
                data-testid="marquee-bottom-toggle"
              />
              Laufband über dem Kontakt-Bereich anzeigen
            </label>
            {draft.marqueeBottom.enabled && (
              <Field label="Einträge des unteren Laufbands (eine Zeile pro Eintrag)" textarea rows={5} value={draft.marqueeBottom.items.join("\n")} onChange={(v) => set(["marqueeBottom", "items"], v.split("\n"))} />
            )}
          </div>
        )}

        {tab === "about" && (
          <div className="max-w-2xl space-y-5" data-testid="admin-panel-about">
            <Field label="Überschrift" value={draft.about.heading} onChange={(v) => set(["about", "heading"], v)} />
            <Field label="Absätze (eine Zeile pro Absatz)" textarea rows={8} value={draft.about.paragraphs.join("\n")} onChange={(v) => set(["about", "paragraphs"], v.split("\n"))} />
            <Field label="Fakten-Liste (eine Zeile pro Punkt)" textarea rows={4} value={draft.about.facts.join("\n")} onChange={(v) => set(["about", "facts"], v.split("\n"))} />
            <Field label="Bild-URL (Porträt)" value={draft.about.portrait_url} onChange={(v) => set(["about", "portrait_url"], v)} />
            <Field label="Bild-Unterschrift" value={draft.about.caption} onChange={(v) => set(["about", "caption"], v)} />
          </div>
        )}

        {tab === "services" && (
          <div className="max-w-2xl space-y-6" data-testid="admin-panel-services">
            {draft.services.map((s, i) => (
              <div key={s.nr} className="space-y-4 border border-foreground/10 p-4">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-primary">Leistung {s.nr}</p>
                <Field label="Titel" value={s.title} onChange={(v) => set(["services", i, "title"], v)} />
                <Field label="Beschreibung" textarea rows={2} value={s.desc} onChange={(v) => set(["services", i, "desc"], v)} />
                <div>
                  <label className={labelCls}>Icon</label>
                  <div className="flex items-center gap-3">
                    <select
                      value={s.icon || "Hammer"}
                      onChange={(e) => set(["services", i, "icon"], e.target.value)}
                      className={inputCls}
                      data-testid={`service-icon-select-${i}`}
                    >
                      {SERVICE_ICONS.map((ic) => (
                        <option key={ic.value} value={ic.value}>
                          {ic.label}
                        </option>
                      ))}
                    </select>
                    {(() => {
                      const Preview = ICON_COMPONENTS[s.icon] || Hammer;
                      return (
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-foreground/15 text-primary">
                          <Preview className="h-5 w-5" />
                        </span>
                      );
                    })()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "beforeAfter" && (
          <div className="max-w-2xl space-y-6" data-testid="admin-panel-beforeAfter">
            <Field label="Überschrift" value={draft.beforeAfter.heading} onChange={(v) => set(["beforeAfter", "heading"], v)} />
            <Field label="Text" textarea rows={3} value={draft.beforeAfter.text} onChange={(v) => set(["beforeAfter", "text"], v)} />
            <Field label="Hinweis unter den Slidern" value={draft.beforeAfter.caption || ""} onChange={(v) => set(["beforeAfter", "caption"], v)} />

            <div className="space-y-4">
              <p className={labelCls}>Vergleiche</p>
              {draft.beforeAfter.items.map((item, i) => (
                <div key={item.id || i} className="space-y-4 border border-foreground/10 p-4" data-testid={`comparison-editor-${i}`}>
                  <div className="flex items-center justify-between">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-primary">Vergleich {i + 1}</p>
                    <button
                      type="button"
                      onClick={() => set(["beforeAfter", "items"], draft.beforeAfter.items.filter((_, j) => j !== i))}
                      disabled={draft.beforeAfter.items.length <= 1}
                      className="rounded-full border border-foreground/15 p-2 text-muted-foreground transition-colors duration-300 hover:border-primary hover:text-primary disabled:opacity-30"
                      aria-label="Vergleich löschen"
                      data-testid={`comparison-delete-${i}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <Field label="Titel (z. B. Badezimmer EG)" value={item.title} onChange={(v) => set(["beforeAfter", "items", i, "title"], v)} />
                  <Field label="Bild-URL Vorher" value={item.before_url} onChange={(v) => set(["beforeAfter", "items", i, "before_url"], v)} />
                  <Field label="Bild-URL Nachher" value={item.after_url} onChange={(v) => set(["beforeAfter", "items", i, "after_url"], v)} />
                  <Field label="Video-URL statt Slider (freiwillig)" value={item.video_url || ""} onChange={(v) => set(["beforeAfter", "items", i, "video_url"], v)} />
                </div>
              ))}
              <button
                type="button"
                onClick={() =>
                  set(["beforeAfter", "items"], [
                    ...draft.beforeAfter.items,
                    { id: String(Date.now()), title: "", before_url: "", after_url: "", video_url: "" },
                  ])
                }
                className="inline-flex items-center gap-2 border border-foreground/15 px-4 py-2 font-mono text-xs uppercase tracking-wider text-muted-foreground transition-colors duration-300 hover:border-primary hover:text-primary"
                data-testid="comparison-add"
              >
                <Plus className="h-4 w-4" /> Vergleich hinzufügen
              </button>
            </div>

            <p className="border border-foreground/10 bg-card p-4 text-xs leading-relaxed text-muted-foreground">
              Tipp: Wenn du eine Video-URL einträgst, ersetzt das Video den Slider. YouTube-Links
              (auch 360°-Videos – Besucher können sich im Video per Ziehen umsehen), Vimeo oder
              direkte MP4-Links funktionieren.
            </p>
          </div>
        )}

        {tab === "contact" && (
          <div className="max-w-2xl space-y-5" data-testid="admin-panel-contact">
            <Field label="Überschrift" value={draft.contact.heading} onChange={(v) => set(["contact", "heading"], v)} />
            <Field label="Text" textarea rows={3} value={draft.contact.text} onChange={(v) => set(["contact", "text"], v)} />
            <Field label="Telefonnummer (Anzeige)" value={draft.contact.phone} onChange={(v) => set(["contact", "phone"], v)} />
            <Field label="WhatsApp-Nummer (international, ohne +)" value={draft.contact.whatsapp} onChange={(v) => set(["contact", "whatsapp"], v)} />
            <Field label="WhatsApp Starttext" value={draft.contact.whatsapp_text} onChange={(v) => set(["contact", "whatsapp_text"], v)} />
            <Field label="Kleiner Text neben dem Button" value={draft.contact.availability} onChange={(v) => set(["contact", "availability"], v)} />
            <Field label="Adresszeile" value={draft.contact.address} onChange={(v) => set(["contact", "address"], v)} />
            <p className="border border-foreground/10 bg-card p-4 text-xs leading-relaxed text-muted-foreground">
              Hinweis: Diese Telefon-/WhatsApp-Nummer steuert auch den Button oben rechts im Kopf
              der Seite. Die Empfänger-Adresse für Formular-E-Mails (hallo@rundum-dabau.de) ist fest
              in der Server-Konfiguration hinterlegt und kann hier nicht geändert werden.
            </p>
          </div>
        )}

        {tab === "businessCard" && (
          <div className="max-w-2xl space-y-5" data-testid="admin-panel-businessCard">
            <Field label="Name (Inhaber)" value={draft.businessCard.name} onChange={(v) => set(["businessCard", "name"], v)} />
            <Field label="Firma" value={draft.businessCard.company} onChange={(v) => set(["businessCard", "company"], v)} />
            <Field label="Slogan / Untertitel" value={draft.businessCard.tagline} onChange={(v) => set(["businessCard", "tagline"], v)} />
            <Field label="Straße & Hausnummer" value={draft.businessCard.street} onChange={(v) => set(["businessCard", "street"], v)} />
            <Field label="PLZ & Ort" value={draft.businessCard.city} onChange={(v) => set(["businessCard", "city"], v)} />
            <Field label="Telefonnummer (Anzeige)" value={draft.businessCard.phone} onChange={(v) => set(["businessCard", "phone"], v)} />
            <Field label="WhatsApp-Nummer (international, ohne +)" value={draft.businessCard.whatsapp} onChange={(v) => set(["businessCard", "whatsapp"], v)} />
            <Field label="WhatsApp Starttext" value={draft.businessCard.whatsapp_text} onChange={(v) => set(["businessCard", "whatsapp_text"], v)} />
            <Field label="Foto-URL (freiwillig, z. B. Porträt)" value={draft.businessCard.photo_url || ""} onChange={(v) => set(["businessCard", "photo_url"], v)} />
            <Field label="Tätigkeiten (eine pro Zeile)" textarea rows={4} value={(draft.businessCard.activities || []).join("\n")} onChange={(v) => set(["businessCard", "activities"], v.split("\n").map((s) => s.trim()).filter(Boolean))} />
            <Field label="E-Mail" value={draft.businessCard.email} onChange={(v) => set(["businessCard", "email"], v)} />
            <Field label="Website" value={draft.businessCard.website} onChange={(v) => set(["businessCard", "website"], v)} />
            <p className="border border-foreground/10 bg-card p-4 text-xs leading-relaxed text-muted-foreground">
              Die digitale Visitenkarte ist unter <span className="text-foreground">/visitenkarte</span> erreichbar
              – ideal als Ziel für QR-Codes auf der Baustelle. Der „Kontakt speichern“-Button dort
              erzeugt automatisch eine vCard-Datei (.vcf) aus genau diesen Angaben.
            </p>
          </div>
        )}

        {tab === "legal" && (
          <div className="max-w-2xl space-y-10" data-testid="admin-panel-legal">
            <div>
              <p className="mb-4 font-mono text-xs uppercase tracking-[0.25em] text-primary">Impressum</p>
              <BlocksEditor blocks={draft.legal.impressum} onChange={(v) => set(["legal", "impressum"], v)} />
            </div>
            <div>
              <p className="mb-4 font-mono text-xs uppercase tracking-[0.25em] text-primary">Datenschutzerklärung</p>
              <BlocksEditor blocks={draft.legal.datenschutz} onChange={(v) => set(["legal", "datenschutz"], v)} />
            </div>
          </div>
        )}

        {tab === "seo" && <SeoPanel draft={draft} set={set} />}

        {tab === "projekte" && <ProjectsPanel authHeaders={authHeaders} />}

        {tab === "kunden" && <CustomersPanel authHeaders={authHeaders} />}

        {tab === "nachrichten" && <MessagesPanel authHeaders={authHeaders} />}

        {tab === "inquiries" && (
          <div data-testid="admin-panel-inquiries">
            {inquiries === null ? (
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">Lade …</p>
            ) : inquiries.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="inquiries-empty">Noch keine Anfragen eingegangen.</p>
            ) : (
              <div className="space-y-4">
                {inquiries.map((iq) => (
                  <div key={iq.id} className="border border-foreground/10 bg-card p-5" data-testid={`inquiry-${iq.id}`}>
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
                      <p className="font-heading text-base font-bold text-foreground">{iq.name}</p>
                      <div className="flex items-center gap-4">
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                          {new Date(iq.created_at).toLocaleString("de-DE")}
                        </p>
                        <button
                          onClick={() => deleteInquiry(iq.id)}
                          data-testid={`inquiry-delete-${iq.id}`}
                          className="rounded-full border border-foreground/15 p-1.5 text-muted-foreground transition-colors duration-300 hover:border-primary hover:text-primary"
                          aria-label="Anfrage löschen"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                    <p className="mb-2 font-mono text-xs text-primary">{iq.contact}</p>
                    <p className="whitespace-pre-line text-sm text-muted-foreground">{iq.message}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "konto" && (
          <form onSubmit={changePw} className="max-w-md space-y-5" data-testid="admin-panel-konto">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">Passwort ändern</p>
            <div>
              <label htmlFor="pw-current" className={labelCls}>Aktuelles Passwort</label>
              <input id="pw-current" data-testid="pw-current-input" type="password" required value={pwForm.current} onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })} className={inputCls} autoComplete="current-password" />
            </div>
            <div>
              <label htmlFor="pw-next" className={labelCls}>Neues Passwort (mind. 8 Zeichen)</label>
              <input id="pw-next" data-testid="pw-next-input" type="password" required minLength={8} value={pwForm.next} onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })} className={inputCls} autoComplete="new-password" />
            </div>
            {pwMsg && <p className="text-sm text-primary" data-testid="pw-message">{pwMsg}</p>}
            <button type="submit" data-testid="pw-change-button" className="bg-primary px-6 py-3 font-heading text-xs font-bold uppercase tracking-wider text-primary-foreground transition-colors duration-300 hover:bg-foreground hover:text-background">
              Passwort ändern
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
